import { computed, ref } from 'vue';
import type { Router } from 'vue-router';
import { formatDateForApi, parseCivilDate } from '@/shared/lib';
import { trainingApi, type CreateRoleChangeTrainingPayload, type TrainingDetail } from '@/entities/Training';
import { useOnboardingMinistrantes, type OnboardingMinistranteRow } from './useOnboardingMinistrantes';
import { useRoleChangeDocuments } from './useRoleChangeDocuments';
import { useTrainingFormResources } from './useTrainingFormResources';
import { useTrainingFormFields } from './useTrainingFormFields';
import { useTrainingFormSubmit } from './useTrainingFormSubmit';
import { useScheduleAndTraineeEditing, buildScheduleSnapshotFields } from './useScheduleAndTraineeEditing';
import { getMemberPositions } from '../lib/getMemberPositions';
import { buildTrainingMatrixRevisionOptionLabel } from '../lib/buildTrainingMatrixRevisionOptionLabel';

const LATEST_REVISIONS_QUICK_PICK_SIZE = 5;

export interface UseRoleChangeFormEditContext {
  trainingId: number;
  initial: TrainingDetail;
  /**
   * Captured synchronously by the edit dispatcher before its await, since this composable
   * is instantiated after one — useRouter() would silently resolve to undefined by then.
   */
  router?: Router;
}

function buildInitialMinistranteRows(initial: TrainingDetail): OnboardingMinistranteRow[] {
  return initial.instructors.map((instructor) => ({
    id: instructor.sessionId,
    responsibleMemberId: instructor.memberId,
    responsibleUserPositionId: instructor.position?.id ?? null,
    trainingDate: parseCivilDate(instructor.trainingDate),
  }));
}

export function useRoleChangeForm(editContext?: UseRoleChangeFormEditContext) {
  const fields = useTrainingFormFields();
  const {
    title,
    notes,
    scheduledByMemberId,
    scheduledByPositionId,
    scheduledDate,
    traineeMemberId,
    traineePositionId,
    trainingMatrixRevisionId,
  } = fields;
  const revisionSearchTerm = ref('');

  const resources = useTrainingFormResources();
  const { members, revisions, loading, memberOptions } = resources;

  const ministrantes = useOnboardingMinistrantes(
    members,
    editContext ? buildInitialMinistranteRows(editContext.initial) : undefined
  );

  const documents = useRoleChangeDocuments(
    traineePositionId,
    trainingMatrixRevisionId,
    editContext ? editContext.initial.documents.map((document) => document.documentRevisionId) : undefined
  );

  const { saving, submit: submitTraining } = useTrainingFormSubmit(editContext?.router);

  const { load: loadScheduleAndTrainee, setScheduledByMemberId, setTraineeMemberId } = useScheduleAndTraineeEditing(
    fields,
    resources,
    editContext
  );

  // useScheduleAndTraineeEditing's load() only awaits resources.load() (members/revisions).
  // documents' own fetch is a reactive side effect of applyInitial() setting the real
  // position/revision ids, not something that base load() knows about — without this,
  // load() could resolve before the documents fetch settles, so useEditTraining.ts's
  // dirty-tracking baseline would capture an empty selection that flips true moments later.
  async function load(): Promise<void> {
    await loadScheduleAndTrainee();
    await documents.whenReady();
  }

  function setRevisionSearchTerm(term: string): void {
    revisionSearchTerm.value = term;
  }

  const scheduledByPositions = computed(() => getMemberPositions(members.value, scheduledByMemberId.value));
  const traineePositions = computed(() => getMemberPositions(members.value, traineeMemberId.value));

  const revisionComboboxOptions = computed(() => {
    const options = revisions.value.map((revision) => ({
      value: String(revision.id),
      label: buildTrainingMatrixRevisionOptionLabel(revision),
    }));
    const term = revisionSearchTerm.value.trim().toLowerCase();
    if (term === '') {
      return options.slice(0, LATEST_REVISIONS_QUICK_PICK_SIZE);
    }
    return options.filter((option) => option.label.toLowerCase().includes(term));
  });

  const canSave = computed(() => {
    return (
      title.value.trim() !== '' &&
      scheduledByMemberId.value !== null &&
      scheduledByPositionId.value !== null &&
      scheduledDate.value !== null &&
      traineeMemberId.value !== null &&
      traineePositionId.value !== null &&
      trainingMatrixRevisionId.value !== null &&
      !documents.loading.value &&
      !documents.error.value &&
      ministrantes.isComplete.value
    );
  });

  function buildMinistranteSessionPayload(row: OnboardingMinistranteRow) {
    if (row.responsibleMemberId === null || row.responsibleUserPositionId === null || row.trainingDate === null) {
      return null;
    }
    const trainingDate = formatDateForApi(row.trainingDate);
    if (trainingDate === null) return null;

    return {
      responsibleMemberId: row.responsibleMemberId,
      responsibleUserPositionId: row.responsibleUserPositionId,
      trainingDate,
    };
  }

  function buildSubmitPayload(): CreateRoleChangeTrainingPayload | null {
    if (
      scheduledByMemberId.value === null ||
      scheduledByPositionId.value === null ||
      scheduledDate.value === null ||
      traineeMemberId.value === null ||
      traineePositionId.value === null ||
      trainingMatrixRevisionId.value === null
    ) {
      return null;
    }

    const apiScheduledDate = formatDateForApi(scheduledDate.value);
    if (apiScheduledDate === null) return null;

    const sessions: CreateRoleChangeTrainingPayload['sessions'] = [];
    for (const row of ministrantes.rows.value) {
      const session = buildMinistranteSessionPayload(row);
      if (session === null) return null;
      sessions.push(session);
    }

    return {
      title: title.value.trim(),
      notes: notes.value.trim() ? notes.value.trim() : undefined,
      scheduledBy: { memberId: scheduledByMemberId.value, positionId: scheduledByPositionId.value },
      plannedDate: apiScheduledDate,
      trainee: { memberId: traineeMemberId.value, positionId: traineePositionId.value },
      trainingMatrixRevisionId: trainingMatrixRevisionId.value,
      sessions,
      documentRevisionIds: documents.selectedDocumentRevisionIds(),
    };
  }

  async function submit(onSuccess?: () => void): Promise<void> {
    if (editContext) {
      await submitTraining(
        canSave.value,
        buildSubmitPayload,
        (payload) => trainingApi.updateRoleChangeTraining(editContext.trainingId, payload),
        { mode: 'edit', onSuccess }
      );
      return;
    }

    await submitTraining(canSave.value, buildSubmitPayload, (payload) =>
      trainingApi.createRoleChangeTraining(payload)
    );
  }

  const snapshot = computed(() =>
    JSON.stringify({
      ...buildScheduleSnapshotFields(fields, ministrantes),
      documentRevisionIds: [...documents.selectedDocumentRevisionIds()].sort((a, b) => a - b),
    })
  );

  return {
    title,
    notes,
    scheduledByMemberId,
    scheduledByPositionId,
    scheduledDate,
    traineeMemberId,
    traineePositionId,
    trainingMatrixRevisionId,
    members,
    revisions,
    revisionComboboxOptions,
    scheduledByPositions,
    traineePositions,
    memberOptions,
    loading,
    saving,
    canSave,
    ministrantes,
    documents,
    snapshot,
    load,
    setScheduledByMemberId,
    setTraineeMemberId,
    setRevisionSearchTerm,
    submit,
  };
}
